/**
 * Kanban drag-start handler: column vs card initialization.
 */

import { useCallback } from 'react';
import { calculateVisibleCardCount, calculateHiddenCounts } from '../../utils';
import { startMissionToSidebarDrag, updateMissionToSidebarPointer } from '../../../../../utils/missionToSidebarBridge';
import { buildDragFreezeSnapshot } from './dragDropUtils';

const useDragStartHandler = ({
  columnIds,
  tasksByStatus,
  tasks,
  campaignId,
  hasSwimlanes,
  swimlanes,
  saveScrollPositions,
  doLockPageScroll,
  setActiveColumnId,
  setActiveCardId,
  setActiveCardSwimlaneId,
  setVisibleCardCount,
  setFrozenTaskCounts,
  setFrozenHiddenCounts,
  lastStatusChangeRef,
}) => {
  const applyDragFreeze = useCallback(() => {
    const snapshot = buildDragFreezeSnapshot(
      tasksByStatus,
      calculateHiddenCounts,
      calculateVisibleCardCount,
    );
    setVisibleCardCount(snapshot.visibleCardCount);
    setFrozenTaskCounts(snapshot.taskCounts);
    setFrozenHiddenCounts(snapshot.hiddenCounts);
  }, [tasksByStatus, setVisibleCardCount, setFrozenTaskCounts, setFrozenHiddenCounts]);

  const handleDragStart = useCallback((event) => {
    const { active } = event;
    const activeId = active.id;
    const activeData = active.data?.current;

    lastStatusChangeRef.current = { taskId: null, columnId: null };

    if (activeData?.type === 'column' || columnIds.includes(activeId)) {
      setActiveColumnId(activeId);
      setActiveCardId(null);
      setActiveCardSwimlaneId(null);
      saveScrollPositions();
      doLockPageScroll();
      applyDragFreeze();
      return;
    }

    setActiveCardId(activeId);
    setActiveColumnId(null);
    saveScrollPositions();
    doLockPageScroll();
    applyDragFreeze();

    if (hasSwimlanes && swimlanes) {
      const cardSwimlane = swimlanes.find((sl) => sl.tasks.some((t) => t.id === activeId));
      setActiveCardSwimlaneId(cardSwimlane ? cardSwimlane.id : null);
    } else {
      setActiveCardSwimlaneId(null);
    }

    const task = (tasks || []).find((t) => t.id === activeId);
    if (task) {
      const sourceSprintId = task.sourceCampaign?.id || campaignId;
      if (sourceSprintId) {
        startMissionToSidebarDrag({
          taskId: task.id,
          sourceSprintId,
          taskName: task.name,
          taskIcon: task.icon,
          source: 'kanban',
          sourceTask: task,
          // Container nodes re-nest their campaign under the drop target
          nestedSprintId: task.nestedSprintId || null,
        });
        const activator = event.activatorEvent;
        if (activator && 'clientX' in activator) {
          updateMissionToSidebarPointer(activator.clientX, activator.clientY);
        }
      }
    }
  }, [
    columnIds,
    saveScrollPositions,
    doLockPageScroll,
    applyDragFreeze,
    hasSwimlanes,
    swimlanes,
    tasks,
    campaignId,
    setActiveColumnId,
    setActiveCardId,
    setActiveCardSwimlaneId,
    lastStatusChangeRef,
  ]);

  return handleDragStart;
};

export default useDragStartHandler;
